import { GameMatchup } from '@optools/components/game-matchup';
import { Badge } from '@optools/components/ui/badge';
import { Card } from '@optools/components/ui/card';
import { Collapsible, CollapsibleContent } from '@optools/components/ui/collapsible';
import { cn } from '@optools/components/ui/utils';
import { BtcScheduleGame } from '@optools/services/operator-gateway/btc/types';
import { formatGameDate } from '@optools/utils/datetime';
import { Link, useLocation } from '@tanstack/react-router';
import { Building, Calendar, CircleAlert, Database, LucideProps, Zap } from 'lucide-react';
import { ForwardRefExoticComponent, ReactNode, RefAttributes, useId } from 'react';

export interface QuickSwitchProps {
  date: string;
  gamePk: number;
  games: BtcScheduleGame[];
}

export function QuickSwitch({ date, gamePk, games }: QuickSwitchProps) {
  const tab = useLocation({
    select: (location) => {
      const matches = location.pathname.match(
        /(pipelines|content|broadcasts|broadcast-acquisitions)/,
      );
      if (matches) return matches[0];
      return 'pipelines';
    },
  });

  return (
    <aside className="-ml-2 hidden overflow-auto pl-2 lg:block">
      <ul className="flex flex-col gap-2">
        {games.map((game) => {
          const { gameDate, status, venue } = game;
          const isSelected = gamePk === game.gamePk;

          const activeAlertCount = game.pipelines.reduce((acc, pipeline) => {
            return acc + pipeline.alerts.filter(({ active }) => active).length;
          }, 0);
          const hasActiveAlerts = activeAlertCount > 0;

          return (
            <li
              key={game.gamePk}
              ref={(ref) => {
                if (isSelected) {
                  ref?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
              }}
              data-testid={`quickswitch-game-${game.gamePk}`}>
              <Collapsible open={isSelected}>
                <Card
                  className={cn('relative px-2 py-3 transition-colors hover:bg-accent/80', {
                    'mt-2.5 border-red-500': hasActiveAlerts,
                    'sticky top-0 border-primary bg-accent hover:bg-accent': isSelected,
                  })}>
                  {!isSelected && (
                    <Link
                      className="absolute inset-0 z-10"
                      from="/"
                      params={{ gamePk: game.gamePk }}
                      replace
                      search={{ date }}
                      to={`/btc-tools/schedule/$gamePk/${tab}`}
                    />
                  )}

                  <GameMatchup
                    awayTeam={game.away}
                    className="mx-auto"
                    feeds={game.feeds}
                    homeTeam={game.home}
                    size={40}
                    withShows={false}
                  />

                  <CollapsibleContent>
                    <dl
                      className="mt-2 flex flex-wrap gap-4 p-2 lg:flex-col"
                      data-testid="game-details">
                      <GameDataTerm icon={Database} label="Game PK" value={game.gamePk} />
                      <GameDataTerm icon={Building} label="Venue" value={venue.name} />
                      <GameDataTerm
                        icon={Calendar}
                        label="Game Date"
                        value={formatGameDate(gameDate)}
                      />
                      <GameDataTerm icon={Zap} label="Game Status" value={status} />
                    </dl>
                  </CollapsibleContent>

                  {hasActiveAlerts && (
                    <div className="absolute top-0 left-0 z-10 flex origin-top-left -translate-x-3 -translate-y-1/2 items-center justify-items-end gap-1">
                      <span
                        className="flex items-center justify-center rounded-full bg-background p-0.5 text-red-500 transition-colors"
                        data-testid="pipeline-alerts-indicator">
                        <CircleAlert className="size-5" />
                      </span>

                      <Badge className="border-red-500 bg-background font-semibold text-red-500 transition-colors">
                        {activeAlertCount} alert{activeAlertCount > 1 ? 's' : ''}
                      </Badge>
                    </div>
                  )}
                </Card>
              </Collapsible>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}

const GameDataTerm = ({
  icon: Icon,
  label,
  value,
}: {
  label: string;
  value: ReactNode;
  icon: ForwardRefExoticComponent<Omit<LucideProps, 'ref'> & RefAttributes<SVGSVGElement>>;
}) => {
  const id = useId();

  return (
    <div>
      <dt className="mb-2 flex items-center gap-1 text-sm text-primary/80" id={id}>
        <Icon className="size-4" />
        {label}
      </dt>
      <dd aria-labelledby={id} className="text-sm">
        {value}
      </dd>
    </div>
  );
};
